import * as React from "react";
import { connect } from "react-redux";
import { Routine } from "redux-saga-routines";
import { injectIntl, WrappedComponentProps } from "react-intl";
import { Action, ActionFunctionAny } from "redux-actions";
import { Field, useFormikContext } from "formik";
import styled, { keyframes } from "styled-components";

import { fetchOrganisation, saveOrganisation } from "../Store/routines";
import { Customer } from "../types";
import { PolicyNameEnum } from "../../PolicyNameEnum";
import { checkPermissionsForCustomer } from "../../Policy/rightsUtil";
import { Checkbox } from "../../../Elements/CheckboxFormik";
import { AppState } from "@Safemate/Store/types";
import UnsavedCheck from "@Safemate/SaveNeeded/unsavedModal";
import { AllPermissions, PolicyContext } from "@Safemate/Policy/Provider";
import { OrganisationForm } from "../General/types";
import Box from "@Elements/Icon/Icons/Box";

interface OrganisationProps<T> extends WrappedComponentProps {
  fetchOrganisation: Routine<ActionFunctionAny<Action<any>>>;
  saveOrganisation: Routine<ActionFunctionAny<Action<any>>>;
  customers: Customer[];
  ownedByCustomer: number;
  ahpId: number;
  deviId: number;
  loaded: boolean;
  deviceSerialNumber: string;
  deviceName: string;
  deviceTypeName: string;
}

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const Wrapper = styled.div`
  width: 100%;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 42px;
  padding: 0 14px;
  margin-bottom: 14px;
  border: ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.colors.borderRadius};
  background: transparent;
  transition: border-color 0.15s ease;

  &:focus-within {
    border: 1px solid ${props => props.theme.colors.accent};
  }
`;

const SearchIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  color: ${props => props.theme.colors.textSecondary};
  opacity: 0.7;

  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  background: transparent;
  outline: none;
  font-size: ${props => props.theme.font.normal};
  color: ${props => props.theme.colors.textPrimary};

  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
    opacity: 0.7;
  }
`;

const ListContainer = styled.div`
  border: ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.colors.borderRadius};
  background: transparent;
  max-height: 460px;
  overflow-y: auto;
`;

const rowPadding = (indent: number) => `11px 16px 11px ${16 + indent * 22}px`;

const OrgRow = styled.button<{ selected?: boolean; indent: number }>`
  width: 100%;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${props => rowPadding(props.indent)};
  background: ${props => (props.selected ? "rgba(128,128,128,0.16)" : "transparent")};
  border: none;
  border-left: 3px solid transparent;
  border-bottom: 1px solid ${props => props.theme.colors.borderColor};
  transition: background 0.12s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &:focus {
    outline: none;
    background: ${props => (props.selected ? "rgba(128,128,128,0.16)" : "rgba(128,128,128,0.1)")};
  }
`;

const OrgRowDisabled = styled.div<{ indent: number }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${props => rowPadding(props.indent)};
  border-left: 3px solid transparent;
  border-bottom: 1px solid ${props => props.theme.colors.borderColor};
  opacity: 0.45;

  &:last-child {
    border-bottom: none;
  }
`;

const RadioDot = styled.span<{ selected?: boolean }>`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${props => (props.selected ? props.theme.colors.accent : props.theme.colors.borderColor)};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => (props.selected ? props.theme.colors.accent : "transparent")};
  }
`;

const OrgName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${props => props.theme.font.normal};
  color: ${props => props.theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: normal;
  word-break: break-word;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CurrentBadge = styled.span`
  flex-shrink: 0;
  font-size: ${props => props.theme.font.small};
  color: ${props => props.theme.colors.accent};
  border: 1px solid ${props => props.theme.colors.accent};
  border-radius: 999px;
  padding: 2px 10px;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.font.normal};
`;

const SkeletonRow = styled.div<{ indent: number }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${props => rowPadding(props.indent)};
  border-bottom: 1px solid ${props => props.theme.colors.borderColor};

  &:last-child {
    border-bottom: none;
  }
`;

const SkeletonDot = styled.div`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${props => props.theme.colors.backgroundTertiary};
`;

const SkeletonBar = styled.div<{ w: number }>`
  height: 12px;
  width: ${props => props.w}%;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.backgroundTertiary} 25%,
    ${props => props.theme.colors.borderColor} 37%,
    ${props => props.theme.colors.backgroundTertiary} 63%
  );
  background-size: 400px 100%;
  animation: ${shimmer} 1.3s ease-in-out infinite;
`;

const CheckWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0 4px;
`;

const MovePreview = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${props => props.theme.colors.borderColor};
`;

const MoveTitle = styled.p`
  margin: 0 0 16px;
  font-size: ${props => props.theme.font.normal};
  color: ${props => props.theme.colors.textPrimary};
  line-height: 1.5;
`;

const MoveFlow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
  flex-wrap: wrap;
`;

const MoveCard = styled.div`
  flex: 1 1 200px;
  min-width: 0;
  border: ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.colors.borderRadius};
  background: transparent;
  padding: 12px 16px;
`;

const MoveLabel = styled.span`
  display: block;
  font-size: ${props => props.theme.font.small};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 4px;
`;

const MoveValue = styled.span`
  font-size: ${props => props.theme.font.normal};
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
  word-break: break-word;
`;

const MoveArrow = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 22px;

  @media (max-width: 520px) {
    transform: rotate(90deg);
  }
`;

export interface OrgValues {
  custId: string;
  updatedAhp: boolean;
  deviId: number;
}

const Organisation = injectIntl(({ intl: { formatMessage }, fetchOrganisation, customers, loaded, ownedByCustomer, ahpId, deviceSerialNumber, deviceName, deviceTypeName }: OrganisationProps<OrgValues>) => {

  const { values, dirty, setFieldValue } = useFormikContext<OrganisationForm>();

  const permissions = React.useContext(PolicyContext) as Map<PolicyNameEnum, AllPermissions>;

  const [ query, setQuery ] = React.useState("");

  React.useEffect(() => {
    fetchOrganisation();
  }, [])

  const customerInfo = React.useMemo(() => {
    const cust: { [index: string]: Customer } = {};
    customers.forEach((customer) => {
      cust[`${customer.customerId}`] = customer;
    })
    return cust;
  }, [customers])

  const [ selectedCustomerAhp, setSelectedCustomerAhp ] = React.useState<number>(ahpId);

  React.useEffect(() => {
    const foundCust = customers.find(cust => `${cust.customerId}` === `${values.custId}`);

    if(foundCust){
      setSelectedCustomerAhp(foundCust.defaultAhp);
    }
  }, [values.custId, customers])

  const normalizedQuery = query.trim().toLowerCase();

  const visibleCustomers = React.useMemo(() => {
    if(!normalizedQuery) return customers;
    return customers.filter(customer => customer.customerName.toLowerCase().includes(normalizedQuery));
  }, [customers, normalizedQuery])

  const renderRow = (customer: Customer) => {
    const disabled = !checkPermissionsForCustomer(permissions, PolicyNameEnum.DEVICE_ORGANIZATION, customer.customerId);
    const indent = normalizedQuery ? 0 : customer.level;
    const isCurrent = customer.customerId === ownedByCustomer;
    const isSelected = `${values.custId}` === `${customer.customerId}`;
    const stockTitle = customer.stock
      ? formatMessage({id: "stockSuborg", defaultMessage: "Lagerunderavdeling"})
      : undefined;

    const currentBadge = isCurrent &&
      <CurrentBadge>{formatMessage({id: "currentOrganization", defaultMessage: "Nåværende"})}</CurrentBadge>;

    if(disabled){
      return (
        <OrgRowDisabled key={customer.customerId} indent={indent} title={stockTitle}>
          <RadioDot />
          <OrgName>
            {customer.customerName}
            {customer.stock && <Box/>}
          </OrgName>
          {currentBadge}
        </OrgRowDisabled>
      )
    }

    return (
      <OrgRow
        type="button"
        role="radio"
        aria-checked={isSelected}
        key={customer.customerId}
        indent={indent}
        selected={isSelected}
        title={stockTitle}
        onClick={() => setFieldValue("custId", `${customer.customerId}`)}
      >
        <RadioDot selected={isSelected} />
        <OrgName>
          {customer.customerName}
          {customer.stock && <Box/>}
        </OrgName>
        {currentBadge}
      </OrgRow>
    )
  }

  return (
    <Wrapper>
      <UnsavedCheck dirtyOverride={() => {
        const cust = customers.find(c => `${c.customerId}` == `${values.custId}`);
        if(cust && cust.stock) return false;
        return dirty;
      }}/>

      <SearchBar>
        <SearchIcon>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 4.99L20.49 19l-4.99-5Zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14Z"/>
          </svg>
        </SearchIcon>
        <SearchInput
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={formatMessage({id: "searchOrganization", defaultMessage: "Søk etter organisasjon"})}
          aria-label={formatMessage({id: "searchOrganization", defaultMessage: "Søk etter organisasjon"})}
        />
      </SearchBar>

      <ListContainer role="radiogroup">
        {!loaded
          ? [40, 62, 48, 55, 70, 45, 58, 50].map((w, i) => (
              <SkeletonRow key={i} indent={i % 3}>
                <SkeletonDot />
                <SkeletonBar w={w} />
              </SkeletonRow>
            ))
          : visibleCustomers.length > 0
            ? visibleCustomers.map(renderRow)
            : <EmptyState>{formatMessage({id: "noOrganizationsFound", defaultMessage: "Ingen organisasjoner funnet"})}</EmptyState>
        }
      </ListContainer>

      {loaded && (selectedCustomerAhp !== ahpId) && dirty &&
        <CheckWrapper>
          <Field
            value={values.updateAhp}
            name="updatedAhp"
            id="updatedAhp"
            label={formatMessage({ id: "ahpCheckBoxLabel", defaultMessage: "Overfør alarmsentral innstillinger til valgt organisasjon" })}
            component={Checkbox}
          />
        </CheckWrapper>}

      {loaded && dirty && customerInfo[values.custId] &&
        <MovePreview>
          <MoveTitle>
            {formatMessage({ id: "moveOrgParagraphOne", defaultMessage: "Ønsker du å flytte" })} {deviceTypeName} '{deviceName}' ({deviceSerialNumber}) {formatMessage({ id: "moveOrgParagraphTwo", defaultMessage: "slik?" })}
          </MoveTitle>
          <MoveFlow>
            <MoveCard>
              <MoveLabel>{formatMessage({ id: "fromOrgLabel", defaultMessage: "Fra" })}</MoveLabel>
              <MoveValue>
                {customerInfo[ownedByCustomer] && customerInfo[ownedByCustomer].customerName}
              </MoveValue>
            </MoveCard>
            <MoveArrow>→</MoveArrow>
            <MoveCard>
              <MoveLabel>{formatMessage({ id: "toOrgLabel", defaultMessage: "Til" })}</MoveLabel>
              <MoveValue>
                {customerInfo[values.custId] && customerInfo[values.custId].customerName}
              </MoveValue>
            </MoveCard>
          </MoveFlow>
        </MovePreview>
      }
    </Wrapper>
  )
})

const mapStateToProps = ({ settings: {organisation: { customers, loaded }, initialization: {deviId}, device: { device: { ownedByCustomer, ahpId, deviceSerialNumber, deviceTypeName, deviceName } } }}: AppState) => {
  return {
    ownedByCustomer,
    loaded,
    customers,
    ahpId,
    deviId,
    deviceSerialNumber, 
    deviceName,
    deviceTypeName
  }
}

const mapDispatchToProps = {
  fetchOrganisation,
  saveOrganisation
};


export default connect(mapStateToProps, mapDispatchToProps)(Organisation);